import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Input, Select, Button, Upload, message, Spin, Space } from 'antd';
import { ArrowRightOutlined, UploadOutlined, CustomerServiceOutlined } from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { supportTicketsApi, show422Error } from '../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const { Option } = Select;
const { TextArea } = Input;

const normFiles = (e) => {
  if (Array.isArray(e)) return e;
  return e?.fileList || [];
};

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'پایین' },
  { value: 'medium', label: 'متوسط' },
  { value: 'high', label: 'بالا' },
  { value: 'urgent', label: 'فوری' },
];

const SupportTicketCreate = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [types, setTypes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      try {
        const res = await supportTicketsApi.getCreateMeta();
        const d = res?.data || res;
        if (cancelled) return;
        setTypes(d.types || []);
        setAgents(d.superadmins || []);
        setCompanies(d.companines || d.companies || []);
        form.setFieldsValue({ priority: 'medium' });
      } catch (error) {
        if (!cancelled) message.error(error?.message || 'خطا در دریافت اطلاعات');
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form]);

  const onFinish = async (values) => {
    const fd = new FormData();
    fd.append('subject', values.subject?.trim() || '');
    fd.append('description', values.description || '');
    fd.append('requested_for', String(values.requested_for ?? ''));
    fd.append('agent_id', String(values.agent_id ?? ''));
    fd.append('type_id', String(values.type_id ?? ''));
    fd.append('priority', values.priority || 'medium');
    const files = values.files || [];
    files.forEach((f) => {
      const file = f?.originFileObj || f;
      if (file && file instanceof Blob) fd.append('file[]', file);
    });
    setSubmitting(true);
    try {
      await supportTicketsApi.create(fd);
      message.success('تیکت ثبت شد');
      navigate('/settings/support-tickets');
    } catch (error) {
      if (error?.name === 'ValidationError' || error?.details) show422Error(error, form);
      else message.error(error?.message || 'خطا در ثبت');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="custom-font w-full min-w-0" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" styles={{ body: { padding: 0 } }}>
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-700 dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-sky-50 dark:bg-sky-900/25">
                <CustomerServiceOutlined className="text-xl text-sky-600 dark:text-sky-400" />
              </div>
              <div className="min-w-0">
                <h1 className="m-0 text-xl font-bold text-gray-800 dark:text-gray-100 sm:text-2xl">تیکت پشتیبانی جدید</h1>
                <p className="m-0 mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">ثبت تیکت برای یک شرکت</p>
              </div>
            </div>
            <Button icon={<ArrowRightOutlined />} onClick={() => navigate('/settings/support-tickets')}>
              بازگشت به لیست
            </Button>
          </div>

          <div className="rounded-b-xl bg-gray-50 p-4 sm:p-6 dark:bg-gray-900/50">
            <Card className="border border-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              {loadingMeta ? (
                <div className="py-16 text-center">
                  <Spin />
                </div>
              ) : (
                <Form form={form} layout="vertical" onFinish={onFinish}>
                  <Form.Item name="subject" label="موضوع" rules={[{ required: true, message: 'موضوع را وارد کنید' }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="description" label="توضیحات" rules={[{ required: true, message: 'توضیحات را وارد کنید' }]}>
                    <TextArea rows={5} />
                  </Form.Item>
                  <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
                    <Form.Item name="requested_for" label="شرکت (درخواست برای)" rules={[{ required: true, message: 'شرکت را انتخاب کنید' }]}>
                      <Select showSearch optionFilterProp="children" popupClassName="custom-font" placeholder="شرکت">
                        {companies.map((c) => (
                          <Option key={c.id} value={c.id}>
                            {c.company_name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="agent_id" label="پشتیبان (سوپرادمین)" rules={[{ required: true, message: 'پشتیبان را انتخاب کنید' }]}>
                      <Select popupClassName="custom-font" placeholder="پشتیبان">
                        {agents.map((a) => (
                          <Option key={a.id} value={a.id}>
                            {a.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="type_id" label="نوع تیکت" rules={[{ required: true, message: 'نوع را انتخاب کنید' }]}>
                      <Select popupClassName="custom-font" placeholder="نوع">
                        {types.map((t) => (
                          <Option key={t.id} value={t.id}>
                            {t.type}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item name="priority" label="اولویت" rules={[{ required: true }]}>
                      <Select popupClassName="custom-font">
                        {PRIORITY_OPTIONS.map((p) => (
                          <Option key={p.value} value={p.value}>
                            {p.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                  <Form.Item name="files" label="فایل‌ها" valuePropName="fileList" getValueFromEvent={normFiles}>
                    <Upload multiple beforeUpload={() => false}>
                      <Button icon={<UploadOutlined />}>انتخاب فایل</Button>
                    </Upload>
                  </Form.Item>
                  <Form.Item className="mb-0">
                    <Space wrap>
                      <Button type="primary" htmlType="submit" loading={submitting} className={PANEL_PRIMARY_BTN_CLASS}>
                        ثبت تیکت
                      </Button>
                      <Button onClick={() => navigate('/settings/support-tickets')}>انصراف</Button>
                    </Space>
                  </Form.Item>
                </Form>
              )}
            </Card>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default SupportTicketCreate;
